// WebAuthn <-> JSON: die Optionen kommen vom Server als JSON (challenge,
// user.id, Credential-IDs base64url), navigator.credentials will
// ArrayBuffer -- und die Antwort muss den umgekehrten Weg. Ohne Stores und
// ohne API: das Dashboard (passkey.ts) und das Recovery-Tool
// (web/recovery) nehmen beide diesen Baustein.

interface PublicKeyCredentialUserEntityJSON {
  id: string;
  name: string;
  displayName: string;
}

interface PublicKeyCredentialDescriptorJSON {
  type: "public-key";
  id: string;
  transports?: string[];
}

interface AuthenticatorSelectionJSON {
  authenticatorAttachment?: AuthenticatorAttachment;
  userVerification?: UserVerificationRequirement;
  residentKey?: ResidentKeyRequirement;
  requireResidentKey?: boolean;
}

interface PublicKeyCredentialCreationOptionsJSON {
  rp: { id?: string; name: string };
  user: PublicKeyCredentialUserEntityJSON;
  challenge: string;
  pubKeyCredParams: { type: "public-key"; alg: number }[];
  timeout?: number;
  excludeCredentials?: PublicKeyCredentialDescriptorJSON[];
  authenticatorSelection?: AuthenticatorSelectionJSON;
  attestation?: AttestationConveyancePreference;
  extensions?: AuthenticationExtensionsClientInputs;
}

export interface PublicKeyOptionsEnvelope {
  publicKey: PublicKeyCredentialCreationOptionsJSON;
}

export function b64urlToBytes(s: string): Uint8Array {
  // padding wieder ergaenzen, dann standard-base64 dekodieren
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const std = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(std);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function bytesToB64url(b: ArrayBuffer): string {
  const bytes = new Uint8Array(b);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function decodeOptions(
  json: PublicKeyCredentialCreationOptionsJSON,
): PublicKeyCredentialCreationOptions {
  return {
    ...json,
    challenge: b64urlToBytes(json.challenge),
    user: {
      ...json.user,
      id: b64urlToBytes(json.user.id),
    },
    excludeCredentials: (json.excludeCredentials ?? []).map((c) => ({
      ...c,
      id: b64urlToBytes(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  } as PublicKeyCredentialCreationOptions;
}

interface RegistrationResponseJSON {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports?: string[];
    publicKey?: string;
    publicKeyAlgorithm?: number;
    authenticatorData?: string;
  };
  authenticatorAttachment?: string;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
}

export function encodeAttestation(c: PublicKeyCredential): RegistrationResponseJSON {
  const r = c.response as AuthenticatorAttestationResponse;
  return {
    id: c.id,
    rawId: bytesToB64url(c.rawId),
    type: c.type,
    response: {
      clientDataJSON: bytesToB64url(r.clientDataJSON),
      attestationObject: bytesToB64url(r.attestationObject),
      transports:
        typeof r.getTransports === "function" ? r.getTransports() : undefined,
      publicKey:
        typeof r.getPublicKey === "function" && r.getPublicKey()
          ? bytesToB64url(r.getPublicKey() as ArrayBuffer)
          : undefined,
      publicKeyAlgorithm:
        typeof r.getPublicKeyAlgorithm === "function"
          ? r.getPublicKeyAlgorithm()
          : undefined,
      authenticatorData:
        typeof r.getAuthenticatorData === "function"
          ? bytesToB64url(r.getAuthenticatorData())
          : undefined,
    },
    authenticatorAttachment: c.authenticatorAttachment ?? undefined,
    clientExtensionResults: c.getClientExtensionResults(),
  };
}

interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: PublicKeyCredentialDescriptorJSON[];
  userVerification?: UserVerificationRequirement;
  extensions?: AuthenticationExtensionsClientInputs;
}

export interface RequestOptionsEnvelope {
  publicKey: PublicKeyCredentialRequestOptionsJSON;
}

export function decodeRequestOptions(
  json: PublicKeyCredentialRequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
  return {
    ...json,
    challenge: b64urlToBytes(json.challenge),
    allowCredentials: (json.allowCredentials ?? []).map((c) => ({
      ...c,
      id: b64urlToBytes(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  } as PublicKeyCredentialRequestOptions;
}

interface AssertionResponseJSON {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle?: string;
  };
  authenticatorAttachment?: string;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
}

export function encodeAssertion(c: PublicKeyCredential): AssertionResponseJSON {
  const r = c.response as AuthenticatorAssertionResponse;
  return {
    id: c.id,
    rawId: bytesToB64url(c.rawId),
    type: c.type,
    response: {
      clientDataJSON: bytesToB64url(r.clientDataJSON),
      authenticatorData: bytesToB64url(r.authenticatorData),
      signature: bytesToB64url(r.signature),
      userHandle: r.userHandle ? bytesToB64url(r.userHandle) : undefined,
    },
    authenticatorAttachment: c.authenticatorAttachment ?? undefined,
    clientExtensionResults: c.getClientExtensionResults(),
  };
}

